'use client';

import { ChangeEvent, FC, useEffect, useState } from 'react';
import { css } from '@bts-web/utils-style-engine';
import { TCryptoTransferTranslations } from '../../../utils/getCryptoTransferTranslations';
import { ButtonBase } from '../../../../common/components/ButtonBase/ButtonBase';
import {
  TActionType,
  TTransfersNextStepStateWithActions,
} from '../../../utils/useTransfersNextStepState.client';
import { TransfersCommonStepsIds, TransfersSendStepsIds } from '../../../types';
import { MainInput } from '../../../../common/components/MainInput/MainInput';
import { WalletsList, WalletAddressItem } from './components/WalletsList';
import { CreateExternalCryptoAddressInput } from '@bts-web/data-layer/server';
import { useTransfersAssetsListContext } from '../../TransfersAssetListContext/TransfersAssetListContext';
import { FilledAdd } from '../../../../common/svgIcons';

interface ChooseRecipientScreenProps {
  state: TTransfersNextStepStateWithActions;
  translations: TCryptoTransferTranslations;
}

const labelStyles = css({
  fontSize: 'body.medium',
  fontWeight: 'body.medium',
  letterSpacing: 'body.medium',
  lineHeight: 'body.medium',
});

const stickyInputsStyle = css({
  position: 'sticky',
  top: 0,
  backgroundColor: 'screen_background.primary',
  zIndex: 1,
  paddingBottom: 'large',
  borderBottom: '1px solid',
  borderBottomColor: 'neutrals.divider',
  px: 'medium',
  py: 'small',
  marginInlineStart: 'calc(var(--spacing-medium) * -1)',
  marginInlineEnd: 'calc(var(--spacing-medium) * -1)',
});

const inputWrapStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'small',
});

const inputStyles = css({
  maxWidth: '100%',
  padding: '12px',
  fontSize: '15px',
  border: '1px solid',
  borderColor: 'neutrals.fill_tertiary',
  borderRadius: '4px',
  marginTop: '2px',
  '&:focus': {
    marginTop: '0',
    border: '2px solid',
    borderColor: 'neutrals.stroke_primary',
  },
  '&[data-invalid=true]': {
    borderColor: 'warning.stroke_primary',
  },
  '&[data-valid=true]': {
    borderColor: 'positive.stroke_primary',
  },
});

const ChooseRecipientScreen: FC<ChooseRecipientScreenProps> = ({
  state,
  translations,
}) => {
  const { bindWalletData, bindNetworkData } = state;

  const [walletAddressInputValue, setWalletAddressInputValue] = useState<
    string | null
  >(null);

  const [selectedWalletAddress] = useState<WalletAddressItem | null>(null);

  const { assetsListForSend } = useTransfersAssetsListContext();

  const assetData = assetsListForSend.find(
    (asset) => asset.id === state.selectedAssetData?.id,
  );

  const hasOneNetwork = assetData?.networkInformation?.networks?.length === 1;

  const chooseNetworkStepActions = {
    pageId: hasOneNetwork
      ? TransfersCommonStepsIds.AssetAndNetworkSummary
      : TransfersCommonStepsIds.ChooseNetwork,
    newBackAction: {
      pageId: TransfersSendStepsIds.ChooseRecipient,
      type: 'page',
    } as TActionType,
  };

  useEffect(() => {
    // Reset state
    state.setAddressInput(null);
  }, []);

  const onWalletAddressInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const addressInputValue = e.target.value;

    // keeps both latin and arabic characters, plus numbers and some special characters
    const sanitizedValue = addressInputValue.replace(
      /[^a-zA-Z0-9_\-. \u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/g,
      '',
    );

    setWalletAddressInputValue(sanitizedValue);
  };

  const handleCreateNewWalletAddress = (type: 'address' | 'name') => {
    if (type === 'address') {
      state.setAddressInput({
        address: walletAddressInputValue,
      } as CreateExternalCryptoAddressInput);

      state.changeToPageAndBindActions(chooseNetworkStepActions);
    }

    if (type === 'name' && walletAddressInputValue) {
      state.setAddressInput({
        ownerLegalName: walletAddressInputValue,
      } as CreateExternalCryptoAddressInput);

      state.changeToPageAndBindActions(chooseNetworkStepActions);
    }
  };

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: 'small',
        flex: 1,
      })}
    >
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
        })}
      >
        <div className={stickyInputsStyle}>
          <div className={inputWrapStyles}>
            <label className={labelStyles} htmlFor="wallet-address">
              {translations.walletAddress}
            </label>

            <div
              className={css({
                display: 'flex',
                justifyContent: 'space-between',
                gap: 'small',
              })}
            >
              <MainInput
                className={`${inputStyles} ${css({
                  width: '85%',
                  backgroundColor: 'white',
                })}`}
                onChange={onWalletAddressInputChange}
                value={walletAddressInputValue || ''}
                id="wallet-address"
                placeholder={translations.walletSearchPlaceholder}
              />

              <ButtonBase
                size="small"
                visual="ghost"
                noPadding
                data-testid="add-new-wallet-button"
                onClick={() => {
                  state.changeToPageAndBindActions(chooseNetworkStepActions);
                }}
              >
                <div
                  className={css({
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'neutrals.fill_quinary',
                    width: '40px',
                    height: '40px',
                    borderRadius: '100px',
                    margin: '0 auto',
                  })}
                >
                  <FilledAdd size={16} />
                </div>
              </ButtonBase>
            </div>
          </div>
        </div>

        <WalletsList
          state={state}
          searchInput={walletAddressInputValue}
          selectedItemAddress={selectedWalletAddress?.address as string}
          onChooseItem={(walletAddress) => {
            // bind network wallet data if wallet is selected
            const walletNetworkData =
              assetData?.networkInformation?.networks?.find(
                (network) => network?.id === walletAddress?.networkId,
              ) as TTransfersNextStepStateWithActions['selectedNetworkData'];

            bindNetworkData(walletNetworkData);

            bindWalletData(walletAddress);

            state.changeToPageAndBindActions({
              pageId: TransfersSendStepsIds.ChooseAmount,
              newBackAction: {
                pageId: TransfersSendStepsIds.ChooseRecipient,
                type: 'page',
              },
            });
          }}
          onCreateNewWalletAddress={handleCreateNewWalletAddress}
          translations={translations}
          hasOneNetwork={hasOneNetwork}
        />
      </div>
    </div>
  );
};

export { ChooseRecipientScreen };
